import {
  FaFlask,
  FaChartLine,
  FaBolt,
  FaCode,
  FaMicrochip,
  FaLightbulb,
} from "react-icons/fa";

/**
 * Single source of truth for the Open Source collection.
 *
 * The hub (`/open-source`) renders its category tabs from this, and every tool
 * page's frame (ToolLayout) builds its sidebar and Prev/Next from the very same
 * list — so the ordering, titles and grouping can never drift between the hub
 * and the tool chrome the way the old hand-maintained links did.
 *
 * `to`   → an internal route; the item is part of the Prev/Next tour.
 * `href` → an external link (e.g. GitHub); shown in the sidebar but skipped by
 *          Prev/Next, since there is no page of ours to step onto.
 *
 * Category order here defines both the tab order and the tour order.
 */
export const OPEN_SOURCE_CATEGORIES = [
  { id: "research", label: "Research", icon: FaFlask },
  { id: "tools", label: "Tools", icon: FaChartLine },
  { id: "libraries", label: "Libraries", icon: FaCode },
  { id: "solutions", label: "Solutions", icon: FaLightbulb },
];

export const OPEN_SOURCE_ITEMS = [
  {
    id: "photoelectric",
    category: "research",
    title: "Photoelectric Effect",
    short: "A wave-based explanation of the photoelectric effect, with open data and code.",
    icon: FaFlask,
    href: "https://github.com/Riviera-Tech-Solutions/wave_explanation_photoelectric_effect",
  },
  {
    id: "electronics",
    category: "tools",
    title: "Electronics Toolkit",
    short: "Ohm's law, LED resistors, dividers, RC constants — a set of quick electronics calculators.",
    icon: FaBolt,
    to: "/open-source/tools/electronics",
  },
  {
    id: "stm32",
    category: "libraries",
    title: "STM32 Embedded C Suite",
    short: "A set of clean, dependency-light embedded C drivers and utilities for STM32.",
    icon: FaMicrochip,
    to: "/open-source/stm32-embedded-c-suite",
  },
  {
    id: "balloon",
    category: "solutions",
    title: "Balloon Generator",
    short: "An open buoyancy-driven generator design, with a calculator to size and build your own.",
    icon: FaLightbulb,
    to: "/open-source/solutions/balloon-generator",
  },
];

/** Items that have a page of ours — the ordered Prev/Next tour. */
export const OPEN_SOURCE_TOUR = OPEN_SOURCE_ITEMS.filter((i) => i.to);

/** Look up an item and its neighbours in the tour by id. */
export function getTourPosition(id) {
  const index = OPEN_SOURCE_TOUR.findIndex((i) => i.id === id);
  if (index === -1) return { item: null, prev: null, next: null, index: -1 };
  return {
    item: OPEN_SOURCE_TOUR[index],
    prev: index > 0 ? OPEN_SOURCE_TOUR[index - 1] : null,
    next: index < OPEN_SOURCE_TOUR.length - 1 ? OPEN_SOURCE_TOUR[index + 1] : null,
    index,
  };
}

/** Items grouped by category, preserving category order. Used by the sidebar. */
export function itemsByCategory() {
  return OPEN_SOURCE_CATEGORIES.map((cat) => ({
    ...cat,
    items: OPEN_SOURCE_ITEMS.filter((i) => i.category === cat.id),
  })).filter((c) => c.items.length > 0);
}
